<template>
  <div class="relative mb-6 space-y-7 md:mb-0">
    <div>
      <SkeletonLoader v-if="isLoading" class="w-full h-[533px]" />

      <div
        v-if="gallery.length > 0"
        :class="twJoin(isLoading ? 'hidden' : 'flex relative')"
      >
        <div
          v-if="sku?.length > 0"
          class="absolute right-0 bottom-0 z-[2]"
          @mouseleave="copyMessage = defaultCopyMessage"
        >
          <VMenu placement="top">
            <a
              class="flex flex-col items-center justify-center text-xxs"
              @click="() => copySkuToClipboard(sku?.join(''))"
            >
              <div>
                <QrcodeVue
                  :value="sku?.join('')"
                  :level="'L'"
                  :render-as="'svg'"
                  :size="45"
                />
              </div>
              <div class="mt-2">{{ sku[0] }}</div>
              <div>{{ sku[1] }}</div>
            </a>
            <template #popper>{{ copyMessage }}</template>
          </VMenu>
        </div>
        <nuxt-error-boundary @error="() => {}">
          <Swiper
            :modules="[
              SwiperEffectCreative,
              SwiperNavigation,
              SwiperThumbs,
              SwiperFreeMode
            ]"
            :space-between="16"
            :thumbs="{ swiper: thumbsSwiper }"
            :navigation="{
              enabled: true,
              disabledClass: 'text-typo-disabled',
              nextEl: '.gallery-c-button-next',
              prevEl: '.gallery-c-button-prev'
            }"
            :creative-effect="{
              prev: {
                shadow: false,
                translate: ['-20%', 0, -1]
              },
              next: {
                shadow: false,
                translate: ['100%', 0, 0]
              }
            }"
          >
            <SwiperSlide
              v-for="(slide, index) in personalizable
                ? [null, ...sortBy(gallery)]
                : [...sortBy(gallery)] || []"
              :key="index"
            >
              <div
                class="block select-none bg-primary-white rounded-lg object-cover cursor-pointer h-[300px] md:h-[533px]"
              >
                <img
                  v-if="!(personalizable && index === 0)"
                  :alt="artworkName?.toLowerCase() ?? 'main product item'"
                  class="object-contain w-full h-full"
                  :src="config.public.mediaUrl + '/' + slide"
                />
                <div class="flex items-center justify-center h-full py-5">
                  <PixiPersonalizeCaseRenderer
                    v-if="!isLoading && personalizable && index === 0"
                    :sku="sku"
                    :template-url="templateUrl"
                    :artwork-template-url="artworkTemplateUrl"
                    :already-save-base64="alreadySaveBase64"
                    @change="(base64) => emit('saveBase64', base64)"
                    @update-thumbnail-base64="
                      (base64) => (personalizeThumbnailBase64 = base64)
                    "
                  />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </nuxt-error-boundary>
      </div>
    </div>
    <SkeletonLoader v-if="isLoading" class="h-[84px] w-full" />
    <div
      v-if="gallery.length > 0"
      :class="twJoin('items-center gap-x-4', isLoading ? 'hidden' : 'flex')"
    >
      <div class="!block gallery-c-button-prev cursor-pointer">
        <Icon name="chevron-left" />
      </div>

      <nuxt-error-boundary @error="() => {}">
        <Swiper
          ref="imageRef"
          :slide-to-clicked-slide="true"
          :modules="[
            SwiperEffectCreative,
            SwiperNavigation,
            SwiperThumbs,
            SwiperFreeMode
          ]"
          :slides-per-view="
            gallery.length + (personalizable ? 1 : 0) < 5 ? gallery.length : 5
          "
          :space-between="16"
          :free-mode="true"
          :watch-slides-progress="true"
          watchslide
          :navigation="{
            enabled: true,
            disabledClass: 'text-typo-disabled',
            nextEl: '.gallery-c-button-next',
            prevEl: '.gallery-c-button-prev'
          }"
          :creative-effect="{
            prev: {
              shadow: false,
              translate: ['-20%', 0, -1]
            },
            next: {
              shadow: false,
              translate: ['100%', 0, 0]
            }
          }"
          :class="twJoin('gallery-swiper')"
          @swiper="setThumbsSwiper"
        >
          <SwiperSlide
            v-for="(slide, index) in personalizable
              ? [templateUrl, ...sortBy(gallery)]
              : [...sortBy(gallery)] || []"
            :key="index"
            class="w-[81px] max-w-[84px]"
          >
            <div
              :class="
                twJoin(
                  'flex items-center md:h-[84px] md:w-[81px] border border-border-divider select-none bg-primary-white rounded-lg object-cover cursor-pointer'
                  // personalizable && index == 0 && 'max-h-[100%]'
                )
              "
            >
              <img
                v-if="slide && index === 0 && personalizable"
                :class="
                  twJoin(
                    'w-full h-full object-contain',
                    personalizable &&
                      index == 0 &&
                      'max-h-[78px] md:max-h-[84px] h-[calc((100vw_-206px)_/_5)] !object-contain py-1 md:py-2.5'
                  )
                "
                :src="personalizeThumbnailBase64"
                :alt="`personalize slide product item index ${index}`"
              />
              <img
                v-else-if="slide"
                :alt="`slide product item index ${index}`"
                :style="
                  personalizable && index == 0
                    ? width < 768
                      ? `height: ${imageThumbHeight - 3}px`
                      : ''
                    : ''
                "
                :src="config.public.mediaUrl + '/' + slide"
                :class="
                  twJoin(
                    'w-full h-full object-contain',
                    personalizable &&
                      index == 0 &&
                      'max-h-[78px] md:max-h-[84px] h-[calc((100vw_-206px)_/_5)] !object-contain py-1 md:py-2.5'
                  )
                "
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </nuxt-error-boundary>
      <div class="!block gallery-c-button-next cursor-pointer">
        <Icon name="chevron-right" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Swiper } from 'swiper/types'
import { twJoin } from 'tailwind-merge'
import QrcodeVue from 'qrcode.vue'
import { sortBy } from 'lodash-es'
import PixiPersonalizeCaseRenderer from './PixiPersonalizeCaseRenderer.vue'

defineProps<{
  isLoading?: boolean
  personalizable?: boolean
  gallery: string[]
  templateUrl?: string | null
  sku: (string | undefined)[]
  alreadySaveBase64?: boolean
  artworkTemplateUrl?: string
  artworkName?: string
}>()

const emit = defineEmits(['saveBase64'])
const personalizeThumbnailBase64 = ref('')

const config = useRuntimeConfig()
const defaultCopyMessage = 'Copy SKU to clipboard'
const imageRef = ref<HTMLDivElement | null>(null)

const { height: imageThumbHeight } = useElementSize(imageRef)
const { width } = useWindowSize()

const copyMessage = ref(defaultCopyMessage)

const thumbsSwiper = ref<Swiper | null>(null)
const setThumbsSwiper = (swiper: Swiper) => {
  thumbsSwiper.value = swiper
}

const copySkuToClipboard = async (sku: string) => {
  try {
    await navigator.clipboard.writeText(sku)
    copyMessage.value = 'Copied!'
  } catch (e) {
    console.log(e)
  }
}
</script>

<style>
.gallery-swiper {
  .swiper-slide-thumb-active > div {
    border-color: #ff4200;
  }
}

.swiper {
  display: grid;
}

.swiper-wrapper {
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
}
</style>
